<script lang="ts">
  import { AlertDialog } from 'bits-ui';
  import { onDestroy } from 'svelte';
  import Button from './Button.svelte';
  import { lockModalViewport } from './modal-viewport';

  let message = $state('');
  let destructive = $state(false);
  let actionLabel = $state('Confirm');
  let resolve = $state<((accepted: boolean) => void) | null>(null);
  let panel = $state<HTMLDivElement | null>(null);
  let cancelButton = $state<HTMLButtonElement | null>(null);
  let returnTarget: HTMLElement | null = null;
  let pointerTarget: HTMLElement | null = null;
  let releaseViewport: (() => void) | undefined;

  export function confirm(
    text: string,
    options: { destructive?: boolean; actionLabel?: string } = {},
  ): Promise<boolean> {
    if (resolve) return Promise.resolve(false);
    message = text;
    destructive = options.destructive ?? false;
    actionLabel = options.actionLabel ?? 'Confirm';
    returnTarget =
      pointerTarget ??
      (document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : null);
    pointerTarget = null;
    return new Promise((done) => {
      resolve = done;
    });
  }

  function finish(accepted: boolean): void {
    const done = resolve;
    resolve = null;
    done?.(accepted);
  }

  function handleOpenAutoFocus(event: Event): void {
    event.preventDefault();
    cancelButton?.focus({ preventScroll: true });
  }

  function handleCloseAutoFocus(event: Event): void {
    event.preventDefault();
    const target =
      returnTarget?.isConnected && !returnTarget.matches(':disabled')
        ? returnTarget
        : document.querySelector<HTMLElement>('.page-back:not(:disabled), #app button:not(:disabled)');
    target?.focus({ preventScroll: true });
    returnTarget = null;
  }

  $effect(() => {
    if (resolve && panel && !releaseViewport) releaseViewport = lockModalViewport(panel);
    if (!resolve && releaseViewport) {
      releaseViewport();
      releaseViewport = undefined;
    }
  });

  onDestroy(() => {
    finish(false);
    releaseViewport?.();
  });
</script>

<svelte:window
  onpointerdowncapture={(event) => {
    if (resolve) return;
    pointerTarget =
      event.target instanceof Element
        ? event.target.closest<HTMLElement>('button, a, input, select, summary')
        : null;
  }}
  onkeydown={() => {
    if (!resolve) pointerTarget = null;
  }}
/>

<AlertDialog.Root open={Boolean(resolve)} onOpenChange={(open) => !open && finish(false)}>
  <AlertDialog.Portal to="#app, #showcase">
    <AlertDialog.Overlay class="modal-overlay alert-dialog-overlay" />
    <AlertDialog.Content
      bind:ref={panel}
      class="surface modal-panel alert-dialog-panel"
      preventScroll={false}
      onOpenAutoFocus={handleOpenAutoFocus}
      onCloseAutoFocus={handleCloseAutoFocus}
    >
      <div class="modal-scroll dialog-content">
        <AlertDialog.Title id="confirmation-title" level={2}>
          {#snippet child({ props })}<h2 {...props}>Are you sure?</h2>{/snippet}
        </AlertDialog.Title>
        <AlertDialog.Description class="confirmation-message">{message}</AlertDialog.Description>
        <div class="row">
          <AlertDialog.Cancel bind:ref={cancelButton} onclick={() => finish(false)}>
            {#snippet child({ props })}
              <Button {...props}>Cancel</Button>
            {/snippet}
          </AlertDialog.Cancel>
          <AlertDialog.Action onclick={() => finish(true)}>
            {#snippet child({ props })}
              <Button {...props} variant={destructive ? 'destructive' : 'primary'}>{actionLabel}</Button>
            {/snippet}
          </AlertDialog.Action>
        </div>
      </div>
    </AlertDialog.Content>
  </AlertDialog.Portal>
</AlertDialog.Root>

<style>
  :global(.modal-overlay.alert-dialog-overlay) {
    z-index: 110;
  }
  :global(.modal-panel.alert-dialog-panel) {
    z-index: 111;
  }
  :global(.confirmation-message) {
    margin: 1em 0;
  }
</style>
